/**
 * WebSocketService - Real-time communication for web chat widget
 */

export class WebSocketService {
	constructor(config = {}) {
		this.baseURL = config.baseURL || '';
		this.workspace = config.workspace || '';
		this.sessionToken = config.sessionToken || null;
		this.mock = config.mock || false;

		this.ws = null;
		this.reconnectAttempts = 0;
		// Reconnect indefinitely with capped exponential backoff. The
		// previous 5-attempt hard cap meant ~31s of churn on a flaky network
		// permanently killed the live-chat connection until page refresh —
		// the dominant "messages don't arrive" symptom in production.
		this.reconnectBaseDelay = 1000;
		this.reconnectMaxDelay = 30_000;
		this.pingInterval = null;
		this.isConnected = false;

		// Heartbeat watchdog. The browser's onclose/onerror only fires when
		// it notices the TCP socket is dead — on stalled-but-not-closed
		// connections (NAT timeouts, sleeping mobile, some intermediaries)
		// that can take many minutes. We track the wall-clock time of the
		// last received frame and force a reconnect if nothing arrives
		// within the timeout. Server's protocol-level pings come every 54s
		// (pkg/websocket/client.go pingPeriod) and our app-level pings/pongs
		// every 30s, so 90s catches a dead pipe with one cycle of headroom.
		this.lastFrameAt = 0;
		this.heartbeatTimeoutMs = 90_000;
		this.heartbeatCheckIntervalMs = 15_000;
		this.heartbeatInterval = null;

		// Event listeners
		this._listeners = new Map();

		// Bind methods
		this._onOpen = this._onOpen.bind(this);
		this._onMessage = this._onMessage.bind(this);
		this._onClose = this._onClose.bind(this);
		this._onError = this._onError.bind(this);
	}

	/**
	 * Connect to WebSocket server
	 */
	connect(sessionToken = null) {
		if (sessionToken) {
			this.sessionToken = sessionToken;
		}

		if (!this.sessionToken) {
			console.warn('[WebSocket] No session token provided');
			return;
		}

		this._intentionallyClosed = false;

		// Mock mode - simulate connection
		if (this.mock) {
			this.isConnected = true;
			this._emit('connected', {});
			this._startMockResponses();
			return;
		}

		// Build WebSocket URL
		const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
		let wsURL = this.baseURL.replace(/^https?:/, wsProtocol);
		wsURL = wsURL.replace('/api/v1', '');
		wsURL = `${wsURL}/api/v1/widget/livechat/ws?token=${encodeURIComponent(this.sessionToken)}`;

		try {
			this.ws = new WebSocket(wsURL);
			this.ws.onopen = this._onOpen;
			this.ws.onmessage = this._onMessage;
			this.ws.onclose = this._onClose;
			this.ws.onerror = this._onError;
		} catch (error) {
			console.error('[WebSocket] Connection error:', error);
			this._scheduleReconnect();
		}
	}

	/**
	 * Disconnect from WebSocket server
	 */
	disconnect() {
		this.isConnected = false;
		this._intentionallyClosed = true; // Prevent reconnection

		if (this.pingInterval) {
			clearInterval(this.pingInterval);
			this.pingInterval = null;
		}

		this._stopHeartbeat();

		if (this.ws) {
			this.ws.close();
			this.ws = null;
		}

		if (this._mockInterval) {
			clearInterval(this._mockInterval);
			this._mockInterval = null;
		}
	}

	_startHeartbeat() {
		this._stopHeartbeat();
		this.lastFrameAt = Date.now();
		this.heartbeatInterval = setInterval(() => {
			if (Date.now() - this.lastFrameAt > this.heartbeatTimeoutMs) {
				console.warn(
					`[WebSocket] No frames in ${this.heartbeatTimeoutMs}ms, forcing reconnect`
				);
				// Closing the socket fires onclose, which schedules a
				// reconnect through the normal path.
				this._stopHeartbeat();
				if (this.ws) {
					try {
						this.ws.close();
					} catch (_) {
						// ignore
					}
				}
			}
		}, this.heartbeatCheckIntervalMs);
	}

	_stopHeartbeat() {
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
			this.heartbeatInterval = null;
		}
	}

	/**
	 * Subscribe to events
	 * @param {string} event - Event name
	 * @param {Function} callback - Event handler
	 * @returns {Function} Unsubscribe function
	 */
	on(event, callback) {
		if (!this._listeners.has(event)) {
			this._listeners.set(event, new Set());
		}
		this._listeners.get(event).add(callback);
		return () => this._listeners.get(event).delete(callback);
	}

	/**
	 * Remove event listener
	 */
	off(event, callback) {
		if (this._listeners.has(event)) {
			this._listeners.get(event).delete(callback);
		}
	}

	/**
	 * Send message through WebSocket
	 */
	send(type, payload = {}) {
		if (!this.isConnected) {
			console.warn('[WebSocket] Not connected, cannot send message');
			return;
		}

		if (this.mock) {
			// Mock mode - just log
			console.log('[WebSocket Mock] Sending:', type, payload);
			return;
		}

		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify({ type, payload }));
		}
	}

	// Private methods

	_onOpen() {
		console.log('[WebSocket] Connected');
		this.isConnected = true;
		this.reconnectAttempts = 0;
		this._intentionallyClosed = false;
		this._startHeartbeat();
		this._emit('connected', {});

		// Start ping interval to keep connection alive
		this.pingInterval = setInterval(() => {
			this.send('ping', {});
		}, 30000);
	}

	_onMessage(event) {
		// Any inbound frame counts as a sign of life for the watchdog —
		// including pongs, message:new, typing, etc.
		this.lastFrameAt = Date.now();
		try {
			const data = JSON.parse(event.data);
			const { type, payload } = data;

			// Handle different event types
			switch (type) {
				case 'message:new':
					this._emit('message', payload);
					break;
				case 'typing:started':
					this._emit('typing_started', payload);
					break;
				case 'typing:stopped':
					this._emit('typing_stopped', payload);
					break;
				case 'conversation:updated':
					this._emit('conversation_updated', payload);
					break;
				case 'conversation:closed':
					this._emit('conversation_closed', payload);
					break;
				case 'availability:changed':
					this._emit('availability_changed', payload);
					break;
				case 'pong':
					// Ping response, ignore
					break;
				default:
					console.log('[WebSocket] Unknown event:', type, payload);
			}
		} catch (error) {
			console.error('[WebSocket] Failed to parse message:', error);
		}
	}

	_onClose(event) {
		console.log('[WebSocket] Disconnected:', event.code, event.reason);
		this.isConnected = false;

		if (this.pingInterval) {
			clearInterval(this.pingInterval);
			this.pingInterval = null;
		}

		this._stopHeartbeat();

		this._emit('disconnected', { code: event.code, reason: event.reason });
		// Skip reconnect if disconnect() was called intentionally.
		if (!this._intentionallyClosed) {
			this._scheduleReconnect();
		}
	}

	_onError(error) {
		console.error('[WebSocket] Error:', error);
		this._emit('error', { error });
	}

	_scheduleReconnect() {
		if (this._intentionallyClosed) return;

		this.reconnectAttempts++;
		const delay = Math.min(
			this.reconnectBaseDelay * Math.pow(2, this.reconnectAttempts - 1),
			this.reconnectMaxDelay
		);
		console.log(
			`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`
		);

		setTimeout(() => {
			this.connect();
		}, delay);
	}

	_emit(event, data) {
		if (this._listeners.has(event)) {
			this._listeners.get(event).forEach((callback) => {
				try {
					callback(data);
				} catch (error) {
					console.error(`[WebSocket] Error in ${event} handler:`, error);
				}
			});
		}
	}

	// Mock support for development
	_startMockResponses() {
		// Simulate agent typing and responses
		this._mockInterval = setInterval(() => {
			// Randomly emit typing or message events for demo
			const random = Math.random();
			if (random < 0.1) {
				this._emit('typing_started', {
					conversation_id: 'conv_1',
					user_id: 'agent_1',
					user_name: 'Sarah',
					is_agent: true,
				});

				// Stop typing after 2 seconds
				setTimeout(() => {
					this._emit('typing_stopped', {
						conversation_id: 'conv_1',
						user_id: 'agent_1',
					});
				}, 2000);
			}
		}, 10000);
	}

	/**
	 * Simulate receiving a message (for mock mode)
	 */
	simulateMessage(conversationId, message) {
		if (this.mock) {
			this._emit('message', {
				conversation_id: conversationId,
				message: {
					id: 'msg_' + Date.now(),
					content: message.content,
					sender_type: message.sender_type || 'agent',
					sender_name: message.sender_name || 'Support',
					sender_avatar: message.sender_avatar || null,
					created_at: new Date().toISOString(),
				},
			});
		}
	}
}
